import { makeAutoObservable } from "mobx";
import { textSpanIntersectsWith } from "typescript";
import { supabaseSelect, wrap } from "../util/supabase-wrapper";
import { EngagementPanel } from "./engagementPanel";
import { globalStores } from "./globalStores";
import { supabase, definitions } from "./supabase/client";
import { EngagementPanel as EngagementPanelStore } from "./engagementPanel";
import jsonHash from "json-stable-stringify";

//TODO: Combine all the auto observable classes to inherit shared stuff from a single class
export class Module {
  module: Partial<definitions["Module"]> = {};
  topic: Partial<definitions["Topic"]> = {};
  level: Partial<definitions["Level"]> = {};
  engagementPanels: Partial<definitions["EngagementPanel"]>[] = [];
  engagementPanelsLoaded: EngagementPanelStore[] = [];
  createdBy: Partial<definitions["StaffDetails"]> = {};
  isLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  async init(id: number) {
    this.isLoading = true;
    this.setId(id);

    const safetyTimeout = setTimeout(() => {
      if (this.isLoading) {
        console.warn("[Module] Init timed out after 15s – clearing loading state");
        this.isLoading = false;
      }
    }, 15000);

    try {
      await Promise.all([
        this.pullModuleDetails(),
        this.preloadEngagementPanels()
      ]);
    } catch (error) {
      console.error("[Module] Init failed:", error);
    }

    clearTimeout(safetyTimeout);
    this.isLoading = false;
    return this;
  }

  private setId(id: number) {
    this.module.id = id;
  }

  async preloadEngagementPanels() {
    try {
      if (!this.module?.id) return;

      // Release old panel references before loading new ones
      this.engagementPanelsLoaded = [];

      const preloadedEngagementPanels = await this.getEngagementPanels();
      this.engagementPanelsLoaded = preloadedEngagementPanels;
    } catch (error) {
      console.error("Failed to preload engagement panels:", error);
    }
  }

  async getEngagementPanels() {
    // Fetch ALL engagement panels for this module in ONE query
    const allPanelsData = wrap(
      await supabase
        .from("EngagementPanel")
        .select("*")
        .match({ module: this.module.id })
    );

    const engagementPanels = await Promise.all(
      allPanelsData.map(async (data) => {
        const ePanel = new EngagementPanel();
        await ePanel.initMetadataOnly(data);
        return ePanel;
      })
    );

    return engagementPanels;
  }

  async addEngagementPanel() {
    if (!this.module.id) {
      throw new Error(
        "ID of module must be populated with init before running!"
      );
    }

    const nextOrderNumber = await EngagementPanel.getNextOrderNumber(
      this.module.id
    );

    const epDetails = wrap(
      await supabase
        .from("EngagementPanel")
        .insert({
          module: this.module.id,
          order: nextOrderNumber,
        })
        .select()
    )[0];

    const newEP = new EngagementPanel();
    globalStores.curEPanel = await newEP.init(epDetails.id);

    await this.pullModuleDetails();

    return newEP;
  }

  /** To make an object the first in the order leave engagemetnPanelId as undefined */
  async reorderAfter(
    beforeEPanel: number | EngagementPanel | undefined,
    ePanelToMove: number | EngagementPanel
  ) {
    const beforeEPanelId =
      typeof beforeEPanel === "number"
        ? beforeEPanel
        : beforeEPanel?.engagementPanel.id;

    const nextEPanelId =
      typeof ePanelToMove === "number"
        ? ePanelToMove
        : ePanelToMove?.engagementPanel.id;

    const curOrderNums = await EngagementPanel.getAllOrderNumbers(
      this.module.id!
    );
    const orderedNums = curOrderNums.sort((a, b) => a.order - b.order);
    const indexOfEPbefore = orderedNums.findIndex(
      (orderNum) => orderNum.id === beforeEPanelId
    );

    let newOrder = 0;

    //TODO: Test all edge cases for engagement panel creation
    if (!beforeEPanelId) {
      //Place at front
      newOrder = orderedNums[0].order / 2;
    } else if (indexOfEPbefore === -1) {
      //Invalid EP id provided
      return;
    } else if (indexOfEPbefore + 1 === orderedNums.length) {
      //Position at end
      newOrder = orderedNums[indexOfEPbefore].order ?? 0 + 1;
    } else {
      //Position between element and next
      newOrder =
        (orderedNums[indexOfEPbefore].order +
          orderedNums[indexOfEPbefore + 1].order) /
        2;
    }

    await supabase.from("EngagementPanel").update({ order: newOrder }).match({
      id: nextEPanelId,
    });

    await this.pullModuleDetails();
  }

  async setCompleted(isCompleted: boolean = true) {
    //TODO: To set a module as completed, need to set question type, question value, question answer type, constant components and blooms taxonomy for all questions
    await supabase
      .from("Module")
      .update({ isCompleted })
      .match({ id: this.module.id })
      .throwOnError();

    await this.pullModuleDetails();
  }

  async remove() {
    await supabase
      .from("Module")
      .delete()
      .match({ id: this.module.id })
      .throwOnError();

    this._remove();
  }

  async clear() {
    this._remove();
  }

  private _remove() {
    this.module = {};
    this.topic = {};
    this.level = {};
    this.engagementPanels = [];
    this.engagementPanelsLoaded = [];
  }

  async pullModuleDetails() {
    //TODO: Split into base details and full details, otherwise may get laggy
    if (!this.module.id) {
      throw new Error(
        "ID of module must be populated with init before running!"
      );
    }

    //WE CANT USE SUPABASE SELECT HERE BECAUSE IT MISSES THE ADDITIONAL ENGAGEMENT PANELS
    const allDetails = wrap(
      await supabaseSelect(
        "Module",
        "*,TeacherAccount(StaffDetails(*)),topic:Topic(*,level:Level(*)),engagementPanels:EngagementPanel(*)",
        this.module.id
      )
    );

    // const allDetails = wrap(
    //   await supabase
    //     .from("Module")
    //     .select(
    //       "*,TeacherAccount(StaffDetails(*)),topic:Topic(*,level:Level(*)),engagementPanels:EngagementPanel(*)"
    //     )
    //     .match({
    //       id: this.module.id,
    //     })
    // );

    // if (jsonHash(allDetailsAlt) !== jsonHash(allDetails)) {
    //   debugger;
    // }

    //TODO: Consider giving the table aliases for ease of conversion later on
    //TODO: Consider making the child accounts, teacher accounts, here into the proper objects
    this.setAllDetails(allDetails);

    return this;
  }

  //TODO: Make proper sync setters and getters for all class variables, otherwise mobx throws a warning
  private setAllDetails(allDetails: any[]) {
    this.topic = allDetails[0].topic;
    this.level = allDetails[0].topic.level;
    this.engagementPanels = allDetails[0].engagementPanels;
    this.createdBy = allDetails[0].TeacherAccount?.StaffDetails;

    //Sort engagement panels
    this.engagementPanels = this.engagementPanels.sort(
      (epA, epB) => epA.order! - epB.order!
    );

    delete allDetails[0].topic;
    delete allDetails[0].level;
    delete allDetails[0].engagementPanels;

    this.module = allDetails[0];
  }
}

export default new Module();
